import { useEffect, useRef } from "react";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import { useTranslation } from "react-i18next";
import Label from "./Label";
import { CalenderIcon } from "../../../icons";
import Hook = flatpickr.Options.Hook;
import DateOption = flatpickr.Options.DateOption;

type PropsType = {
  id: string;
  mode?: "single" | "multiple" | "range" | "time";
  onChange?: Hook | Hook[];
  defaultDate?: DateOption;
  label?: string;
  placeholder?: string;
  className?: string;
  calendarClassName?: string;
  static?: boolean;
};

export default function DatePicker({
  id,
  mode,
  onChange,
  label,
  defaultDate,
  placeholder,
  className,
  static: isStatic = false, // Default to false for better modal compatibility
}: PropsType) {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === "es";

  const inputClasses = `field-base field-default h-11 appearance-none px-4 py-2.5 ${className || ""}`;

  const fpInstance = useRef<flatpickr.Instance | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!inputRef.current) return;
    const isTimeMode = mode === "time";

    const config: flatpickr.Options.Options = {
      mode: mode === "time" ? "single" : (mode || "single"),
      static: isStatic,
      dateFormat: isTimeMode ? "H:i" : "Y-m-d",
      enableTime: isTimeMode,
      noCalendar: isTimeMode,
      altInput: true,
      altInputClass: inputClasses,
      altFormat: isTimeMode ? "H:i" : (isSpanish ? "d-m-Y" : "Y-m-d"),
      locale: isSpanish ? Spanish : undefined,
      defaultDate,
      onChange,
      onClose: onChange,
      time_24hr: true,
      allowInput: true,
      disableMobile: true,
    };

    // If not static, append to body to avoid clipping in modals
    if (!isStatic) {
      config.appendTo = document.body;
    }

    const instance = flatpickr(inputRef.current, config);

    if (!Array.isArray(instance)) {
      fpInstance.current = instance;
    }

    return () => {
      if (fpInstance.current) {
        fpInstance.current.destroy();
        fpInstance.current = null;
      }
    };
  }, [mode, onChange, defaultDate, isSpanish, inputClasses, isStatic]);

  const Icon = mode === "time" ? (
    <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ) : <CalenderIcon className="size-5" />;

  return (
    <div className="w-full">
      {label && <Label htmlFor={id}>{label}</Label>}

      <div className="relative w-full">
        {/* Hidden from React's side too. flatpickr hides this input and shows
            its own formatted copy (`altInput`) in its place; but React strips
            the `type` attribute on every re-render of an input that has no
            `type` prop, so any re-render that did not re-create the picker
            brought this raw Y-m-d field back on screen, stacked above the
            real one. Saying `hidden` here makes React agree with flatpickr. */}
        <input
          ref={inputRef}
          id={id}
          type="hidden"
          placeholder={placeholder}
          className={inputClasses}
        />

        <span className="absolute text-content-tertiary -translate-y-1/2 pointer-events-none right-3.5 top-1/2 dark:text-gray-500">
          {Icon}
        </span>
      </div>
    </div>
  );
}
